import { useEffect, useState } from 'react';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import MagazinePageHeader from './MagazinePageHeader';
import MagazineNewButton from './MagazineNewButton';
import MagazineFormModal from './MagazineFormModal';
import { useAuth } from '../../hooks/useAuth';
import {
  createSlot,
  deleteSlot,
  getIssues,
  getSlots,
  updateSlot,
} from '../../api/magazineService';

const emptyForm = {
  issue_id: '',
  label: '',
  size: '',
  status: 'active',
};

const Inventory = () => {
  const { canManageMagazineCatalog } = useAuth();
  const [issues, setIssues] = useState<any[]>([]);
  const [filterIssueId, setFilterIssueId] = useState('');
  const [items, setItems] = useState<any[]>([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadIssues = async () => {
    const data = await getIssues();
    setIssues(data);
  };

  const loadSlots = async (issueId?: string) => {
    setLoading(true);
    const data = await getSlots(issueId);
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    loadIssues();
    loadSlots();
  }, []);

  useEffect(() => {
    loadSlots(filterIssueId || undefined);
  }, [filterIssueId]);

  const closeForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(false);
  };

  const openNewForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setShowForm(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canManageMagazineCatalog) return;
    const payload = {
      issue_id: Number(form.issue_id),
      label: form.label,
      size: form.size,
      status: form.status,
    };
    if (editingId) {
      await updateSlot(editingId, payload);
    } else {
      await createSlot(payload);
    }
    closeForm();
    loadSlots(filterIssueId || undefined);
  };

  const handleEdit = (item: any) => {
    setEditingId(item.id);
    setForm({
      issue_id: String(item.issue_id),
      label: item.label,
      size: item.size || '',
      status: item.status || 'active',
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (!canManageMagazineCatalog || !window.confirm('Delete this ad slot?')) return;
    await deleteSlot(id);
    loadSlots(filterIssueId || undefined);
  };

  return (
    <Layout>
      <MagazinePageHeader
        title="Inventory"
        description="Manage ad slots and availability for each issue."
        action={canManageMagazineCatalog ? <MagazineNewButton onClick={openNewForm} /> : undefined}
      />

      <PageCard className="mb-6">
        <label className="mb-1 block text-sm font-medium text-gray-600">Filter by issue</label>
        <select
          className="w-full max-w-md rounded border border-gray-300 px-3 py-2 text-sm"
          value={filterIssueId}
          onChange={(e) => setFilterIssueId(e.target.value)}
        >
          <option value="">All issues</option>
          {issues.map((issue) => (
            <option key={issue.id} value={issue.id}>
              {issue.publication?.name ? `${issue.publication.name} — ` : ''}{issue.title}
            </option>
          ))}
        </select>
      </PageCard>

      {canManageMagazineCatalog && (
        <MagazineFormModal
          open={showForm}
          title={editingId ? 'Edit ad slot' : 'New ad slot'}
          onClose={closeForm}
        >
          <form onSubmit={handleSubmit} className="grid min-w-0 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">Issue</label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={form.issue_id}
                onChange={(e) => setForm({ ...form, issue_id: e.target.value })}
                required
              >
                <option value="">Select issue</option>
                {issues.map((issue) => (
                  <option key={issue.id} value={issue.id}>
                    {issue.publication?.name ? `${issue.publication.name} — ` : ''}{issue.title}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">Product</label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
                placeholder="e.g. Full page"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">Size/Type</label>
              <input
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={form.size}
                onChange={(e) => setForm({ ...form, size: e.target.value })}
                placeholder="e.g. A4"
              />
            </div>
            {/* <div>
              <label className="mb-1 block text-sm font-medium text-gray-600">Status</label>
              <select
                className="w-full rounded border border-gray-300 px-3 py-2 text-sm"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div> */}
            <div className="flex gap-2 sm:col-span-2">
              <button
                type="submit"
                className="rounded bg-torch-primary px-4 py-2 text-sm font-medium text-white hover:bg-torch-primary-dark"
              >
                {editingId ? 'Update' : 'Create'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                className="rounded border border-gray-300 px-4 py-2 text-sm text-gray-700"
              >
                Cancel
              </button>
            </div>
          </form>
        </MagazineFormModal>
      )}

      <PageCard>
        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No ad slots yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-2 pr-4">Issue</th>
                  <th className="py-2 pr-4">Label</th>
                  <th className="py-2 pr-4">Size</th>
                  <th className="py-2 pr-4">Price</th>
                  <th className="py-2 pr-4">Available</th>
                  <th className="py-2 pr-4">Status</th>
                  {canManageMagazineCatalog && <th className="py-2">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4 text-gray-900">
                      {item.issue?.publication?.name ? `${item.issue.publication.name} — ` : ''}
                      {item.issue?.title || '—'}
                    </td>
                    <td className="py-3 pr-4 font-medium text-gray-900">{item.label}</td>
                    <td className="py-3 pr-4 text-gray-600">{item.size || '—'}</td>
                    <td className="py-3 pr-4 text-gray-600">{item.price}</td>
                    <td className="py-3 pr-4 text-gray-600">
                      {item.quantity - item.booked_count} / {item.quantity}
                    </td>
                    <td className="py-3 pr-4 capitalize text-gray-600">{item.status}</td>
                    {canManageMagazineCatalog && (
                      <td className="py-3">
                        <div className="flex gap-2">
                          <button onClick={() => handleEdit(item)} className="text-sm text-torch-primary hover:underline">Edit</button>
                          <button onClick={() => handleDelete(item.id)} className="text-sm text-red-600 hover:underline">Delete</button>
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>
    </Layout>
  );
};

export default Inventory;
